import { useEffect, useState } from "react";
import { Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { SetPasswordDialog } from "@/components/common/PasswordDialogs";

export default function Staff() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const load = () => api.get("/restaurant/staff").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const wrap = async (fn, msg) => { try { await fn(); toast.success(msg); load(); return true; } catch (e) { toast.error(apiError(e)); return false; } };
  const add = async (e) => {
    e.preventDefault();
    if (await wrap(() => api.post("/restaurant/staff", { ...f, permissions: ["orders"] }), "Staff added")) setF({ name: "", email: "", password: "" });
  };
  const patch = (s, upd) => wrap(() => api.patch(`/restaurant/staff/${s.id}`, { name: s.name, permissions: s.permissions, is_active: s.is_active, ...upd }), "Updated");

  if (!items) return <Loader />;
  return (
    <div>
      <PageHeader title="Staff" subtitle="Staff can only see and update orders for your restaurant." />
      <form onSubmit={add} className="mb-6 grid gap-2 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <Input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" />
        <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email-input" />
        <Input required type="password" minLength={8} placeholder="Password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password-input" />
        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="staff-add-btn"><Plus className="mr-1 h-4 w-4" />Add staff</Button>
      </form>
      {items.length === 0 ? <EmptyState icon={Users} title="No staff yet" /> : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="staff-list">
          {items.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-4 px-4 py-3" data-testid={`staff-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800">{s.name}</p>
                <p className="text-xs text-slate-400">{s.email}</p>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-500">Order access
                <Switch checked={s.permissions.includes("orders")} onCheckedChange={(v) => patch(s, { permissions: v ? ["orders"] : [] })} data-testid={`staff-perm-${s.id}`} />
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-500">Active
                <Switch checked={s.is_active} onCheckedChange={(v) => patch(s, { is_active: v })} data-testid={`staff-active-${s.id}`} />
              </label>
              <SetPasswordDialog title={`Reset password for ${s.name}`} testId={`staff-reset-pw-${s.id}`} triggerLabel="Reset password"
                description="Set a new password and share it with this staff member. They will be signed out everywhere."
                onSubmit={(pw) => patch(s, { password: pw })} />
              <ConfirmButton title={`Remove ${s.name}?`} actionLabel="Remove" onConfirm={() => wrap(() => api.delete(`/restaurant/staff/${s.id}`), "Removed")} className="p-1.5 text-slate-400 hover:text-rose-600" testId={`staff-delete-${s.id}`}><Trash2 className="h-4 w-4" /></ConfirmButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
